import BoldSmallText from '@/components/BoldSmallText';
import { PlanMessage } from '@/components/PlanMessage';
import RegularText from '@/components/RegularText';
import { config } from '@/config';
import { PricingPlan } from '@/constants/enum';
import { useDeleteGoogleAccountMutation, useGetGoogleAccountQuery, useGetGoogleConnectUrlQuery } from '@/redux/query';
import { BlockStack, Button, Card, FormLayout, InlineGrid, InlineStack, TextField, Thumbnail } from '@shopify/polaris';
import { LogoGoogleIcon } from '@shopify/polaris-icons';

import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface ConnectGoogleProps {
  calendar: {
    time: string;
    timeBackup: string;
    disable: boolean;
  };
  setTime: Dispatch<
    SetStateAction<{
      time: string;
      timeBackup: string;
      disable: boolean;
    }>
  >;
}

const ConnectGoole = ({ calendar, setTime }: ConnectGoogleProps) => {
  const { t } = useTranslation('orderReport');
  const { data: googleUrlData } = useGetGoogleConnectUrlQuery({ shop: config.shop });
  const {
    data: googleAccountData,
    isError: isErrorGoogleAccount,
    refetch: refetchGoogleAccountData,
  } = useGetGoogleAccountQuery({ shop: config.shop });
  const [deleteGoogleAccount, { isLoading: isDeleteGoogleAccountLoading }] = useDeleteGoogleAccountMutation();
  const googleUrl = useMemo(() => googleUrlData?.data.url, [googleUrlData?.data.url]);

  const handleDisconnectGoogleAccount = () => {
    deleteGoogleAccount({ shop: config.shop })
      .unwrap()
      .then(() => refetchGoogleAccountData());
  };

  return (
    <Card>
      <BlockStack gap={!isErrorGoogleAccount && googleAccountData ? '200' : '400'}>
        {!isErrorGoogleAccount && googleAccountData ? (
          <InlineGrid columns="1fr auto">
            <BlockStack gap="200" inlineAlign="start" align="center">
              <InlineStack gap="200">
                <Thumbnail source={googleAccountData.data.avatar} alt="Google connect user avatar" size="small" />
                <BlockStack gap="100">
                  <BoldSmallText>{googleAccountData.data.name}</BoldSmallText>
                  <RegularText tone="subdued">{googleAccountData.data.email}</RegularText>
                </BlockStack>
              </InlineStack>
              <Button variant="plain" url="https://calendar.google.com/calendar" target="_blank" role="link">
                {t('syncGoogle.calendarLink')}
              </Button>
            </BlockStack>
            <BlockStack gap="100">
              <Button icon={LogoGoogleIcon} loading={isDeleteGoogleAccountLoading} onClick={handleDisconnectGoogleAccount}>
                {t('syncGoogle.disconnectButton')}
              </Button>
            </BlockStack>
          </InlineGrid>
        ) : (
          <InlineGrid columns="1fr auto">
            <BlockStack gap="200">
              <BoldSmallText>{t('syncGoogle.title')}</BoldSmallText>
              <RegularText>{t('syncGoogle.helpText')}</RegularText>
            </BlockStack>
            <BlockStack align="center" gap={'200'}>
              {calendar.disable && <PlanMessage plan={PricingPlan.Smart} />}
              <Button variant="primary" icon={LogoGoogleIcon} onClick={() => window.open(googleUrl)} disabled={calendar.disable}>
                {t('syncGoogle.connectButton')}
              </Button>
            </BlockStack>
          </InlineGrid>
        )}
        <FormLayout>
          <TextField
            label={t('syncGoogle.timeTitle')}
            value={calendar.time}
            onChange={(value) => setTime((prev) => ({ ...prev, time: value }))}
            type="time"
            autoComplete="off"
            disabled={isErrorGoogleAccount || !googleAccountData}
          />
        </FormLayout>
      </BlockStack>
    </Card>
  );
};

export default ConnectGoole;
